import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import type {
  MeasurementVerdict,
  MonthlyReportChange,
  MonthlyReportLimitation,
  MonthlyReportV1,
  RecommendationType,
} from "@siteagain/shared-types";

import { intlLocale, useLocale, type Locale } from "./i18n";
import { formatDay, formatMonth } from "./operator/format";
import "./monthly-report.css";

/**
 * The monthly report, rendered. Used by the customer's app and by the operator's review
 * screen, so what an operator approves is exactly what the customer reads.
 *
 * It states what the snapshot holds and nothing else. A section with nothing in it is
 * left out; a quiet month says so in one sentence rather than padding.
 */
const EN = {
  title: (month: string) => `What was done for your website — ${month}`,
  quietLead: (month: string) => `No changes went live in ${month}, and nothing is waiting for your decision.`,
  lead: (shipped: number, rolledBack: number, measured: number) => {
    const parts: string[] = [];
    if (shipped > 0) parts.push(`${shipped} ${shipped === 1 ? "change went" : "changes went"} live`);
    if (rolledBack > 0) parts.push(`${rolledBack} ${rolledBack === 1 ? "was" : "were"} rolled back`);
    if (measured > 0) parts.push(`${measured} ${measured === 1 ? "result was" : "results were"} measured`);
    return parts.length > 0 ? `${parts.join(", ")}.` : "";
  },
  totals: { shipped: "Went live", rolledBack: "Rolled back", measured: "Measured", improved: "Improved", waiting: "Waiting for you" },
  shipped: "What went live",
  rolledBack: "What was rolled back",
  rolledBackNote: "These changes went live and were then undone. The previous version was restored.",
  notShipped: "What did not go live",
  notShippedNote: "These were started and stopped before publishing. Nothing reached your site.",
  outcome: { rejected: "Declined", failed: "Stopped on an error" },
  measurements: "Measured results",
  measurementLine: (days: number, verdict: string, confidence: string) => `After ${days} days: ${verdict} (${confidence} confidence)`,
  verdict: { improved: "improved", neutral: "no clear change", declined: "declined", insufficient: "not enough data" } satisfies Record<MeasurementVerdict, string>,
  confidence: { high: "high", medium: "medium", low: "low", insufficient: "insufficient" } as Record<string, string>,
  score: "Audit score",
  scoreChange: (before: number, end: number, delta: number) => `${before} → ${end} (${delta > 0 ? "+" : ""}${delta})`,
  scoreEnd: (score: number, day: string) => `${score} on ${day}`,
  scoreBefore: (score: number, day: string) => `Previous audit: ${score} on ${day}`,
  scoreNone: "No audit completed this month.",
  scoreNoBefore: "There was no earlier audit to compare with.",
  waiting: "Waiting for you",
  waitingSince: (day: string) => `since ${day}`,
  openRecommendations: (count: number) => `${count} ${count === 1 ? "recommendation is" : "recommendations are"} open.`,
  expired: (count: number) => `${count} ${count === 1 ? "recommendation expired" : "recommendations expired"} without a decision.`,
  pages: "Pages",
  evidence: "Why it was suggested",
  origin: { recommendation: "From a recommendation", request: "You asked for it" },
  type: {
    metadata: "Titles & descriptions", schema: "Structured data", internal_link: "Internal links", content: "Content",
    technical: "Technical", performance: "Performance", structural: "Structure", new_page: "New page", redirect: "Redirect",
  } satisfies Record<RecommendationType, string>,
  limitsTitle: "What this report does not cover",
  limitation: {
    no_traffic_data: "It does not include search traffic or rankings.",
    observational_only: "Measured results compare audits, search traffic and enquiries before and after a change. They are observations, not proof that the change caused them.",
    summaries_in_original_language: "Descriptions of changes appear in the language they were written in.",
    search_data_incomplete: "Google publishes its numbers a few days late, so the last days of the month are not in the search figures.",
    no_enquiry_data: "It does not include enquiries: your website's contact form is not connected to SiteAgain yet.",
  } satisfies Record<MonthlyReportLimitation, string>,
  enquiries: {
    title: "Enquiries from your website",
    intro: "People who got in touch through your contact form. These are counts, not causes.",
    total: "Enquiries",
    qualified: "Qualified",
    won: "Became customers",
    fromSearch: "From search",
    previous: (count: string) => `Previous month: ${count}`,
    noComparison: "No earlier month to compare",
    since: (day: string) => `Counted since ${day}.`,
    pages: "Where visitors came in",
    enquiryUnit: (count: string, n: number) => `${count} ${n === 1 ? "enquiry" : "enquiries"}`,
    measured: "After our changes",
    measuredLine: (before: string, after: string, verdict: string, confidence: string) =>
      `28 days after: ${verdict}. Enquiries ${before} → ${after} (${confidence} confidence).`,
    verdict: { improved: "more enquiries than before", neutral: "about the same", declined: "fewer enquiries than before", insufficient: "not enough to say" } satisfies Record<MeasurementVerdict, string>,
  },
  search: {
    title: "Search on Google",
    intro: "Real numbers from your Search Console. They show what moved, not why.",
    clicks: "Clicks",
    impressions: "Appearances",
    ctr: "Click rate",
    position: "Average position",
    versus: "vs the month before",
    noComparison: "No earlier month to compare",
    through: (day: string) => `Numbers run through ${day}.`,
    pages: "Pages people landed on",
    queries: "What people searched for",
    clicksUnit: (count: string) => `${count} clicks`,
    measured: "After our changes",
    measuredLine: (days: number, verdict: string, confidence: string) => `After ${days} days: ${verdict} (${confidence} confidence)`,
    pagesScope: (pages: string) => `Pages: ${pages}`,
    siteScope: "Whole site",
    verdict: { improved: "more visits than the rest of the site", neutral: "no clear difference", declined: "fewer visits than the rest of the site", insufficient: "not enough to say" } satisfies Record<MeasurementVerdict, string>,
  },
  generated: (day: string) => `Written on ${day} from the records of what was published.`,
  print: "Print or save as PDF",
};

type ReportCopy = typeof EN;

const FR: ReportCopy = {
  title: (month) => `Ce qui a été fait pour votre site — ${month}`,
  quietLead: (month) => `Aucune modification n'est passée en ligne en ${month}, et rien n'attend votre décision.`,
  lead: (shipped, rolledBack, measured) => {
    const parts: string[] = [];
    if (shipped > 0) parts.push(`${shipped} ${shipped === 1 ? "modification est passée" : "modifications sont passées"} en ligne`);
    if (rolledBack > 0) parts.push(`${rolledBack} ${rolledBack === 1 ? "a été annulée" : "ont été annulées"}`);
    if (measured > 0) parts.push(`${measured} ${measured === 1 ? "résultat a été mesuré" : "résultats ont été mesurés"}`);
    return parts.length > 0 ? `${parts.join(", ")}.` : "";
  },
  totals: { shipped: "En ligne", rolledBack: "Annulées", measured: "Mesurées", improved: "En progrès", waiting: "Vous attendent" },
  shipped: "Ce qui est passé en ligne",
  rolledBack: "Ce qui a été annulé",
  rolledBackNote: "Ces modifications sont passées en ligne puis ont été annulées. La version précédente a été restaurée.",
  notShipped: "Ce qui n'est pas passé en ligne",
  notShippedNote: "Ces modifications ont été commencées puis arrêtées avant publication. Rien n'a atteint votre site.",
  outcome: { rejected: "Refusée", failed: "Arrêtée sur une erreur" },
  measurements: "Résultats mesurés",
  measurementLine: (days, verdict, confidence) => `Après ${days} jours : ${verdict} (confiance ${confidence})`,
  verdict: { improved: "en progrès", neutral: "pas de changement net", declined: "en recul", insufficient: "données insuffisantes" },
  confidence: { high: "élevée", medium: "moyenne", low: "faible", insufficient: "insuffisante" },
  score: "Score d'audit",
  scoreChange: (before, end, delta) => `${before} → ${end} (${delta > 0 ? "+" : ""}${delta})`,
  scoreEnd: (score, day) => `${score} le ${day}`,
  scoreBefore: (score, day) => `Audit précédent : ${score} le ${day}`,
  scoreNone: "Aucun audit terminé ce mois-ci.",
  scoreNoBefore: "Il n'y avait pas d'audit antérieur à comparer.",
  waiting: "Vous attendent",
  waitingSince: (day) => `depuis le ${day}`,
  openRecommendations: (count) => `${count} ${count === 1 ? "recommandation est ouverte" : "recommandations sont ouvertes"}.`,
  expired: (count) => `${count} ${count === 1 ? "recommandation a expiré" : "recommandations ont expiré"} sans décision.`,
  pages: "Pages",
  evidence: "Pourquoi elle a été proposée",
  origin: { recommendation: "Issue d'une recommandation", request: "À votre demande" },
  type: {
    metadata: "Titres et descriptions", schema: "Données structurées", internal_link: "Liens internes", content: "Contenu",
    technical: "Technique", performance: "Performance", structural: "Structure", new_page: "Nouvelle page", redirect: "Redirection",
  },
  limitsTitle: "Ce que ce rapport ne couvre pas",
  limitation: {
    no_traffic_data: "Il n'inclut ni le trafic de recherche, ni les positions.",
    observational_only: "Les résultats mesurés comparent des audits, le trafic de recherche et les demandes avant et après une modification. Ce sont des observations, pas la preuve que la modification en est la cause.",
    summaries_in_original_language: "Les descriptions des modifications sont affichées dans la langue où elles ont été écrites.",
    search_data_incomplete: "Google publie ses chiffres avec quelques jours de retard : les derniers jours du mois ne sont pas dans les chiffres de recherche.",
    no_enquiry_data: "Il n'inclut pas les demandes : le formulaire de contact de votre site n'est pas encore relié à SiteAgain.",
  },
  enquiries: {
    title: "Les demandes reçues par votre site",
    intro: "Les personnes qui vous ont contacté par votre formulaire de contact. Ce sont des comptages, pas des causes.",
    total: "Demandes",
    qualified: "Qualifiées",
    won: "Devenues clientes",
    fromSearch: "Via la recherche",
    previous: (count) => `Mois précédent : ${count}`,
    noComparison: "Pas de mois précédent à comparer",
    since: (day) => `Comptage depuis le ${day}.`,
    pages: "Par où les visiteurs sont arrivés",
    enquiryUnit: (count, n) => `${count} demande${n === 1 ? "" : "s"}`,
    measured: "Après nos modifications",
    measuredLine: (before, after, verdict, confidence) =>
      `28 jours après : ${verdict}. Demandes ${before} → ${after} (confiance ${confidence}).`,
    verdict: { improved: "plus de demandes qu'avant", neutral: "à peu près pareil", declined: "moins de demandes qu'avant", insufficient: "pas assez pour conclure" },
  },
  search: {
    title: "La recherche sur Google",
    intro: "Les chiffres réels de votre Search Console. Ils montrent ce qui a bougé, pas pourquoi.",
    clicks: "Clics",
    impressions: "Affichages",
    ctr: "Taux de clic",
    position: "Position moyenne",
    versus: "par rapport au mois précédent",
    noComparison: "Pas de mois précédent à comparer",
    through: (day) => `Les chiffres vont jusqu'au ${day}.`,
    pages: "Pages d'arrivée",
    queries: "Ce que les gens ont cherché",
    clicksUnit: (count) => `${count} clics`,
    measured: "Après nos modifications",
    measuredLine: (days, verdict, confidence) => `Après ${days} jours : ${verdict} (confiance ${confidence})`,
    pagesScope: (pages) => `Pages : ${pages}`,
    siteScope: "Tout le site",
    verdict: { improved: "plus de visites que le reste du site", neutral: "pas de différence nette", declined: "moins de visites que le reste du site", insufficient: "pas assez pour conclure" },
  },
  generated: (day) => `Rédigé le ${day} à partir des enregistrements de ce qui a été publié.`,
  print: "Imprimer ou enregistrer en PDF",
};

export function reportCopy(locale: Locale): ReportCopy {
  return locale === "fr" ? FR : EN;
}

export function MonthlyReportView({ report, showPrint = true }: { report: MonthlyReportV1; showPrint?: boolean }) {
  const locale = useLocale();
  const t = reportCopy(locale);
  const month = formatMonth(report.period.month, locale);
  const lead = report.quiet ? t.quietLead(month) : t.lead(report.totals.shipped, report.totals.rolledBack, report.totals.measured);
  const waitingCount = report.waiting.awaitingApproval.length;

  return (
    <article className="mr" lang={locale}>
      <header className="mr-head">
        <small>{report.siteName}{report.domain ? ` · ${report.domain}` : ""}</small>
        <h2>{t.title(month)}</h2>
        {lead && <p className="mr-lead">{lead}</p>}
        {showPrint && (
          <button type="button" className="mr-print" onClick={() => window.print()}>{t.print}</button>
        )}
      </header>

      {!report.quiet && (
        <dl className="mr-totals">
          <Total label={t.totals.shipped} value={report.totals.shipped} tone="good" />
          <Total label={t.totals.rolledBack} value={report.totals.rolledBack} tone={report.totals.rolledBack > 0 ? "warn" : "quiet"} />
          <Total label={t.totals.measured} value={report.totals.measured} tone="quiet" />
          <Total label={t.totals.improved} value={report.totals.measuredImproved} tone="good" />
          <Total label={t.totals.waiting} value={waitingCount} tone={waitingCount > 0 ? "warn" : "quiet"} />
        </dl>
      )}

      {report.shipped.length > 0 && (
        <Section title={t.shipped}>
          <ul className="mr-list">{report.shipped.map((change) => <ChangeItem key={change.changeSetId} change={change} t={t} />)}</ul>
        </Section>
      )}
      {report.rolledBack.length > 0 && (
        <Section title={t.rolledBack} note={t.rolledBackNote}>
          <ul className="mr-list">{report.rolledBack.map((change) => <ChangeItem key={change.changeSetId} change={change} t={t} />)}</ul>
        </Section>
      )}
      {report.notShipped.length > 0 && (
        <Section title={t.notShipped} note={t.notShippedNote}>
          <ul className="mr-list">
            {report.notShipped.map((item) => (
              <li key={item.changeSetId}>
                <p>{item.summary}</p>
                <span className="mr-chip">{t.outcome[item.outcome]}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}
      {report.measurements.length > 0 && (
        <Section title={t.measurements}>
          <ul className="mr-list">
            {report.measurements.map((measurement) => (
              <li key={`${measurement.recommendationId}:${measurement.windowDays}`}>
                <p>{measurement.title}</p>
                <span className={`mr-chip verdict-${measurement.verdict}`}>
                  {t.measurementLine(measurement.windowDays, t.verdict[measurement.verdict], t.confidence[measurement.confidence] ?? measurement.confidence)}
                </span>
                {measurement.caveat && <p className="mr-caveat">{measurement.caveat}</p>}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {report.search && <SearchSection search={report.search} t={t} locale={locale} />}

      {report.enquiries && <EnquirySection enquiries={report.enquiries} periodStart={report.period.start} t={t} locale={locale} />}

      <Section title={t.score}>
        <ScoreBlock report={report} locale={locale} t={t} />
      </Section>

      {(waitingCount > 0 || report.waiting.openRecommendations > 0 || report.waiting.expiredRecommendations > 0) && (
        <Section title={t.waiting}>
          {waitingCount > 0 && (
            <ul className="mr-list">
              {report.waiting.awaitingApproval.map((item) => (
                <li key={item.changeSetId}>
                  <p>{item.summary}</p>
                  <span className="mr-chip">{t.waitingSince(formatDay(item.since, locale))}</span>
                </li>
              ))}
            </ul>
          )}
          {report.waiting.openRecommendations > 0 && <p className="mr-plain">{t.openRecommendations(report.waiting.openRecommendations)}</p>}
          {report.waiting.expiredRecommendations > 0 && <p className="mr-plain">{t.expired(report.waiting.expiredRecommendations)}</p>}
        </Section>
      )}

      <footer className="mr-limits">
        <h3>{t.limitsTitle}</h3>
        <ul>{report.limitations.map((limitation) => <li key={limitation}>{t.limitation[limitation]}</li>)}</ul>
        <p>{t.generated(formatDay(report.generatedAt, locale))}</p>
      </footer>
    </article>
  );
}

function Total({ label, value, tone }: { label: string; value: number; tone: "good" | "warn" | "quiet" }) {
  return <div className={`mr-total tone-${tone}`}><dd>{value}</dd><dt>{label}</dt></div>;
}

function Section({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <section className="mr-section">
      <h3>{title}</h3>
      {note && <p className="mr-note">{note}</p>}
      {children}
    </section>
  );
}

function ChangeItem({ change, t }: { change: MonthlyReportChange; t: ReportCopy }) {
  return (
    <li>
      <p>{change.summary}</p>
      <div className="mr-meta">
        <span className="mr-chip">{t.origin[change.origin]}</span>
        {change.recommendationType && <span className="mr-chip">{t.type[change.recommendationType]}</span>}
        {change.pages.length > 0 && <span className="mr-pages">{t.pages}: {change.pages.join(", ")}</span>}
      </div>
      {change.evidence.length > 0 && (
        <details className="mr-evidence">
          <summary>{t.evidence}</summary>
          <ul>{change.evidence.map((line) => <li key={line}>{line}</li>)}</ul>
        </details>
      )}
    </li>
  );
}

function ScoreBlock({ report, locale, t }: { report: MonthlyReportV1; locale: Locale; t: ReportCopy }) {
  const { before, end, delta } = report.score;
  if (!end || end.score === null) {
    return (
      <>
        <p className="mr-plain">{t.scoreNone}</p>
        {before?.score != null && <p className="mr-plain">{t.scoreBefore(before.score, formatDay(before.capturedAt, locale))}</p>}
      </>
    );
  }
  return (
    <>
      {before?.score != null && delta !== null
        ? <p className="mr-score">{t.scoreChange(before.score, end.score, delta)}</p>
        : <p className="mr-score">{end.score}</p>}
      <p className="mr-plain">{t.scoreEnd(end.score, formatDay(end.capturedAt, locale))}</p>
      {before?.score != null && <p className="mr-plain">{t.scoreBefore(before.score, formatDay(before.capturedAt, locale))}</p>}
      {before === null && <p className="mr-plain">{t.scoreNoBefore}</p>}
    </>
  );
}

/**
 * The frame a report is read in, for the customer and for the operator who reviews it.
 *
 * It renders into `document.body`, outside the app, so printing can show the report alone and
 * nothing of the page behind it. Escape and a click on the backdrop close it; focus starts on
 * the close button.
 */
export function ReportDialog({
  label,
  eyebrow,
  badges,
  closeLabel,
  onClose,
  footer,
  children,
}: {
  label: string;
  eyebrow: string;
  badges?: React.ReactNode;
  closeLabel: string;
  onClose(): void;
  footer?: React.ReactNode;
  children: React.ReactNode;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => { closeRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div className="mr-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="mr-modal" role="dialog" aria-modal="true" aria-label={label}>
        <header className="mr-modal-head">
          <div>
            <small>{eyebrow}</small>
            {badges && <p className="mr-modal-status">{badges}</p>}
          </div>
          <button ref={closeRef} type="button" className="mr-modal-close" aria-label={closeLabel} onClick={onClose}>×</button>
        </header>
        <div className="mr-modal-body">{children}</div>
        {footer && <footer className="mr-modal-foot">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}

function SearchSection({ search, t, locale }: { search: NonNullable<MonthlyReportV1["search"]>; t: ReportCopy; locale: Locale }) {
  const copy = t.search;
  const number = new Intl.NumberFormat(intlLocale(locale));
  const previous = search.previousMonth;
  const change = (current: number, earlier: number | null | undefined): string | null => {
    if (earlier == null || earlier <= 0) return null;
    const percent = Math.round(((current - earlier) / earlier) * 100);
    return `${percent > 0 ? "+" : ""}${percent}%`;
  };
  const ctr = search.totals.ctr === null ? "—" : `${(search.totals.ctr * 100).toFixed(1)}%`;
  const tiles: Array<{ label: string; value: string; change: string | null }> = [
    { label: copy.clicks, value: number.format(search.totals.clicks), change: change(search.totals.clicks, previous?.clicks) },
    { label: copy.impressions, value: number.format(search.totals.impressions), change: change(search.totals.impressions, previous?.impressions) },
    { label: copy.ctr, value: ctr, change: null },
    { label: copy.position, value: search.totals.position === null ? "—" : search.totals.position.toFixed(1), change: null },
  ];
  return (
    <Section title={copy.title} note={copy.intro}>
      <dl className="mr-totals mr-search-tiles">
        {tiles.map((tile) => (
          <div className="mr-total tone-quiet" key={tile.label}>
            <dd>{tile.value}</dd>
            <dt>{tile.label}{tile.change ? ` · ${tile.change}` : ""}</dt>
          </div>
        ))}
      </dl>
      <p className="mr-plain">{previous ? copy.versus : copy.noComparison}. {copy.through(formatDay(search.dataThrough, locale))}</p>
      <div className="mr-search-lists">
        {([[copy.pages, search.topPages], [copy.queries, search.topQueries]] as const).map(([title, rows]) => rows.length > 0 && (
          <div key={title}>
            <h4>{title}</h4>
            <ol>{rows.map((row) => <li key={row.key}><span>{row.key}</span><strong>{copy.clicksUnit(number.format(row.clicks))}</strong></li>)}</ol>
          </div>
        ))}
      </div>
      {search.measurements.length > 0 && (
        <>
          <h4>{copy.measured}</h4>
          <ul className="mr-list">
            {search.measurements.map((item) => (
              <li key={`${item.changeSetId}:${item.windowDays}`}>
                <p>{item.scope === "pages" ? copy.pagesScope(item.pages.slice(0, 3).join(", ")) : copy.siteScope}</p>
                <span className={`mr-chip verdict-${item.verdict}`}>
                  {copy.measuredLine(item.windowDays, copy.verdict[item.verdict], t.confidence[item.confidence] ?? item.confidence)}
                </span>
                {item.caveat && <p className="mr-caveat">{item.caveat}</p>}
              </li>
            ))}
          </ul>
        </>
      )}
    </Section>
  );
}

function EnquirySection({
  enquiries,
  periodStart,
  t,
  locale,
}: {
  enquiries: NonNullable<MonthlyReportV1["enquiries"]>;
  periodStart: string;
  t: ReportCopy;
  locale: Locale;
}) {
  const copy = t.enquiries;
  const number = new Intl.NumberFormat(intlLocale(locale));
  const tiles: Array<{ label: string; value: number }> = [{ label: copy.total, value: enquiries.total }];
  if (enquiries.total > 0) {
    tiles.push({ label: copy.qualified, value: enquiries.qualified }, { label: copy.won, value: enquiries.won });
    if (enquiries.fromSearch !== null) tiles.push({ label: copy.fromSearch, value: enquiries.fromSearch });
  }
  // Listening began part-way through the month: say so, so a low count is not read as a quiet month.
  const partial = enquiries.collectingSince > periodStart;
  return (
    <Section title={copy.title} note={copy.intro}>
      <dl className="mr-totals mr-search-tiles">
        {tiles.map((tile) => (
          <div className="mr-total tone-quiet" key={tile.label}><dd>{number.format(tile.value)}</dd><dt>{tile.label}</dt></div>
        ))}
      </dl>
      <p className="mr-plain">
        {enquiries.previousMonth === null ? copy.noComparison : copy.previous(number.format(enquiries.previousMonth))}.
        {partial ? ` ${copy.since(formatDay(enquiries.collectingSince, locale))}` : ""}
      </p>
      {enquiries.topPages.length > 0 && (
        <div className="mr-search-lists">
          <div>
            <h4>{copy.pages}</h4>
            <ol>{enquiries.topPages.map((row) => <li key={row.key}><span>{row.key}</span><strong>{copy.enquiryUnit(number.format(row.count), row.count)}</strong></li>)}</ol>
          </div>
        </div>
      )}
      {enquiries.measurements.length > 0 && (
        <>
          <h4>{copy.measured}</h4>
          <ul className="mr-list">
            {enquiries.measurements.map((item) => (
              <li key={`${item.changeSetId}:${item.windowDays}`}>
                <span className={`mr-chip verdict-${item.verdict}`}>
                  {copy.measuredLine(number.format(item.before.total), number.format(item.after.total), copy.verdict[item.verdict], t.confidence[item.confidence] ?? item.confidence)}
                </span>
                {item.caveat && <p className="mr-caveat">{item.caveat}</p>}
              </li>
            ))}
          </ul>
        </>
      )}
    </Section>
  );
}
